import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  CreditCard,
  RefreshCw,
  Users,
  TrendingUp,
  DollarSign,
  Crown,
  Zap,
  Star,
  Building2,
  UserCheck,
  Edit,
  Check,
  X,
  Percent
} from "lucide-react";

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch subscription tiers
      const tiersRes = await fetch(`${API}/admin/subscription-tiers`, { credentials: "include" });
      if (tiersRes.ok) {
        setTiers(await tiersRes.json());
      }

      // Fetch users for subscription management
      const usersRes = await fetch(`${API}/admin/users?limit=100`, { credentials: "include" });
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch subscription data:", error);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubscription = async (userId, newTier) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/subscription`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subscription_tier: newTier })
      });
      if (res.ok) {
        toast.success("Subscription updated!");
        fetchData();
      } else {
        toast.error("Failed to update subscription");
      }
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  // Filter users by search query
  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchQuery || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Calculate subscription breakdown
  const paidUsers = users.filter(u => u.subscription_tier && !u.subscription_tier.includes("free"));
  const freeUsers = users.filter(u => !u.subscription_tier || u.subscription_tier.includes("free"));

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-subscriptions-page">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                SUBSCRIPTION <span className="text-crimson">MANAGEMENT</span>
              </h1>
              <p className="text-gray-400">Monitor and manage user subscriptions</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#333] text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {[
              { id: "overview", label: "Overview", icon: CreditCard },
              { id: "tiers", label: "Pricing Tiers", icon: DollarSign },
              { id: "users", label: "User Subscriptions", icon: Users },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`subscription-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-crimson text-white"
                    : "bg-obsidian-light text-gray-400 hover:text-white"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="text-xs">Total Users</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{stats?.users?.total || 0}</div>
                </motion.div>
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Crown className="w-4 h-4" />
                    <span className="text-xs">Paid Users</span>
                  </div>
                  <div className="text-2xl font-bold text-green-400">{stats?.subscriptions?.paid || 0}</div>
                </motion.div>
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Zap className="w-4 h-4" />
                    <span className="text-xs">Free Users</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-400">{stats?.subscriptions?.free || 0}</div>
                </motion.div>
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Percent className="w-4 h-4" />
                    <span className="text-xs">Conversion Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-crimson">
                    {stats?.users?.total ? ((stats?.subscriptions?.paid / stats?.users?.total) * 100).toFixed(1) : 0}%
                  </div>
                </motion.div>
              </div>

              {/* Conversion Progress */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">Subscription Distribution</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Free Users</span>
                      <span className="text-white">{stats?.subscriptions?.free || 0}</span>
                    </div>
                    <div className="h-3 bg-obsidian rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gray-500" 
                        style={{ 
                          width: `${stats?.users?.total ? (stats?.subscriptions?.free / stats?.users?.total * 100) : 0}%` 
                        }} 
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Paid Users</span>
                      <span className="text-green-400">{stats?.subscriptions?.paid || 0}</span>
                    </div>
                    <div className="h-3 bg-obsidian rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500" 
                        style={{ 
                          width: `${stats?.users?.total ? (stats?.subscriptions?.paid / stats?.users?.total * 100) : 0}%` 
                        }} 
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Tiers Tab */}
          {activeTab === "tiers" && (
            <div className="space-y-6">
              {/* Job Seeker Tiers */}
              <div>
                <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-green-400" />
                  Job Seeker Plans
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tiers?.jobseeker?.map((tier, i) => (
                    <motion.div
                      key={tier.id}
                      className={`bg-obsidian-light border p-6 ${
                        tier.price === 0 ? "border-[#222]" : "border-green-500/30"
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-heading text-lg font-bold">{tier.name}</h4>
                        {tier.price > 0 && <Crown className="w-5 h-5 text-yellow-400" />}
                      </div>
                      <div className="text-3xl font-bold text-white mb-2">
                        ${tier.price}<span className="text-sm text-gray-500">/mo</span>
                      </div>
                      <p className="text-sm text-gray-400">Tier ID: {tier.id}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Recruiter Tiers */}
              <div>
                <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-400" />
                  Recruiter Plans
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {tiers?.recruiter?.map((tier, i) => (
                    <motion.div
                      key={tier.id}
                      className={`bg-obsidian-light border p-6 ${
                        tier.price === 0 ? "border-[#222]" : "border-blue-500/30"
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-heading text-lg font-bold">{tier.name}</h4>
                        {tier.price >= 199 && <Star className="w-5 h-5 text-yellow-400" />}
                      </div>
                      <div className="text-3xl font-bold text-white mb-2">
                        ${tier.price}<span className="text-sm text-gray-500">/mo</span>
                      </div>
                      <p className="text-sm text-gray-400">Tier ID: {tier.id}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative max-w-md">
                <Input
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-4 bg-obsidian-light border-[#333] text-white"
                />
              </div>

              {/* Users Table */}
              <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#333]">
                      <th className="text-left p-4 text-gray-400 font-medium">User</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Role</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Current Plan</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Change Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-[#222] hover:bg-white/5">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Users className="w-5 h-5 text-crimson" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-white">{u.name || "No name"}</div>
                              <div className="text-sm text-gray-500">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs uppercase ${
                            u.role === "admin" ? "bg-yellow-500/20 text-yellow-400" :
                            u.role === "recruiter" ? "bg-blue-500/20 text-blue-400" :
                            "bg-green-500/20 text-green-400"
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs ${
                            u.subscription_tier?.includes("free") || !u.subscription_tier
                              ? "bg-gray-500/20 text-gray-400"
                              : "bg-green-500/20 text-green-400"
                          }`}>
                            {u.subscription_tier || "free"}
                          </span>
                        </td>
                        <td className="p-4">
                          <select
                            value={u.subscription_tier || ""}
                            onChange={(e) => updateUserSubscription(u.user_id, e.target.value)}
                            className="px-3 py-1 bg-obsidian border border-[#333] text-white text-sm"
                          >
                            <optgroup label="Job Seeker Tiers">
                              {tiers?.jobseeker?.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Recruiter Tiers">
                              {tiers?.recruiter?.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </optgroup>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredUsers.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No users found matching your search
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminSubscriptions;
